import { GraphQLClient } from 'graphql-request';
import { COMPANY_ID_HEADER } from '@helix/auth';
import { resolveConfiguredOrLocalApiBaseUrl } from './local-api-origin';
import {
  TENANT_ID_LOCAL_STORAGE_KEY,
  USER_ID_LOCAL_STORAGE_KEY,
  isHelixE2eModeEnabled,
  isLocalRuntimeUatBypassRequested,
  resolveLocalUatAuthContext,
} from './local-uat-auth';

export const USER_ID_HEADER = 'x-user-id';
export { TENANT_ID_LOCAL_STORAGE_KEY, USER_ID_LOCAL_STORAGE_KEY };

export const getAuthHeaders = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  if (!isHelixE2eModeEnabled() && !isLocalRuntimeUatBypassRequested(localStorage)) {
    return {};
  }

  const headers: Record<string, string> = {};
  const authContext = resolveLocalUatAuthContext(localStorage);
  const tenantId = authContext?.tenantId;
  const userId = authContext?.userId;

  if (tenantId && tenantId !== 'undefined') headers[COMPANY_ID_HEADER] = tenantId;
  if (userId && userId !== 'undefined') headers[USER_ID_HEADER] = userId;

  if (authContext?.testUserHeader) {
    headers['x-helix-test-user'] = authContext.testUserHeader;
  }
  return headers;
};

/**
 * Convert a graphql-request failure into an error that is safe to RENDER.
 *
 * `ClientError.message` embeds the entire JSON response — including any server
 * `extensions` — plus the request document. Rethrowing it raw painted a full
 * server stacktrace with filesystem paths into the browser on /capabilities
 * (#4008 SFIX defect 2). Only the human GraphQL error message(s) survive; the
 * raw error goes to the console for debugging, never to the page.
 */
export function sanitizeGraphQLError(error: unknown, fallback: string): Error {
  if (typeof console !== 'undefined') {
    // Full fidelity for developers — DevTools only, never the DOM.
    console.error(fallback, error);
  }
  const responseErrors = (error as { response?: { errors?: unknown } })?.response?.errors;
  const messages = Array.isArray(responseErrors)
    ? responseErrors
        .map((item) => String((item as { message?: unknown })?.message ?? '').trim())
        .filter((message) => message.length > 0)
    : [];
  if (messages.length > 0) {
    return new Error(`${fallback}: ${messages.join('; ')}`);
  }
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return new Error(
    typeof status === 'number' ? `${fallback} (HTTP ${status}).` : `${fallback}.`,
  );
}

export const getGraphQLClient = () => {
  const apiBase = resolveConfiguredOrLocalApiBaseUrl(process.env.NEXT_PUBLIC_API_URL || 'http://127.0.0.1:4000');
  return new GraphQLClient(`${apiBase}/graphql`, {
    fetch: (input: RequestInfo | URL, init?: RequestInit) => {
      const headers = new Headers(init?.headers);
      const extraHeaders = getAuthHeaders();
      for (const [key, value] of Object.entries(extraHeaders)) {
        headers.set(key, value);
      }

      return fetch(input, {
        ...init,
        credentials: 'include',
        headers,
      });
    },
  });
};
